/**
 * TradeProposalsCard — the shared, two-sided trade-agreement surface on Compare.
 *
 * Unlike the Trade Match card (built from each user's private earmark list), a
 * proposal is a single record BOTH trainers see. This card renders the live
 * proposals between the viewer and this friend and drives the handshake:
 *
 *   pending  → recipient: [Agree] / [Decline]      · proposer: waiting + [Cancel]
 *   agreed   → each side: [Mark completed] once traded in-game (two tick states)
 *   completed→ "Trade complete"
 *
 * Marking a side complete updates only THAT trainer's own Dex (handled by the
 * parent via markTradeCompletion) plus records the confirmation server-side.
 *
 * EVERY ROW ALSO SAYS HOW IT WAS SHARED, and offers the other way. A trade can
 * go out twice — put to the trainer in app AND sent as a card to a chat outside
 * it — and the two were invisible to each other: the Trade Match card was the
 * only place a card could be exported, and it dropped the pairing the moment it
 * was proposed, so "I asked, now let me send them the picture too" had nowhere
 * to happen. See lib/tradeShareMethods for the colour language and
 * lib/tradeShareLog for why the card half is device-local.
 */
import { speciesName } from "@/data/speciesNames";
import { ChanceOnlyBadge } from "@/components/trade/ChanceOnlyBadge";
import { WishlistSprite } from "@/components/WishlistSprite";
import type { PokedexVariant } from "@/lib/pogoPokedexVariantClassifier";
import { tradePairingKey, useTradeShareLog, type TradePairing } from "@/lib/tradeShareLog";
import {
  shareMethodAlpha,
  TRADE_SHARE_METHODS,
  type TradeShareMethod,
} from "@/lib/tradeShareMethods";
import { ArrowRight, Check, Clock, X, Handshake } from "lucide-react";

export interface TradeProposal {
  id: number;
  fromAccountId: number;
  toAccountId: number;
  giveSpeciesId: number;
  giveVariant: string;
  getSpeciesId: number;
  getVariant: string;
  status: "pending" | "agreed" | "declined" | "cancelled" | "completed";
  fromCompletedAt: number | null;
  toCompletedAt: number | null;
  createdAt: number;
  updatedAt: number;
}

export function TradeProposalsCard({
  proposals,
  myAccountId,
  friendName,
  busyId,
  onAgree,
  onDecline,
  onCancel,
  onComplete,
  onShareCard,
}: {
  proposals: TradeProposal[];
  myAccountId: number | null;
  friendName: string;
  busyId?: number | null;
  onAgree: (id: number) => void;
  onDecline: (id: number) => void;
  onCancel: (id: number) => void;
  onComplete: (p: TradeProposal) => void;
  /** Export this row's swap as a share card. Omit and the offer is not shown. */
  onShareCard?: (pairing: TradePairing) => void;
}) {
  if (myAccountId == null || proposals.length === 0) return null;
  return (
    <section className="mt-5 v2-neon-card-soft p-4" data-testid="section-trade-proposals">
      <div className="flex items-center gap-2 mb-1">
        <Handshake className="w-3.5 h-3.5" style={{ color: "hsl(142 100% 60%)" }} />
        <h2 className="v2-display-up text-[11px]" style={{ color: "var(--accent-fill)" }}>
          Trade proposals
        </h2>
      </div>
      <p className="text-[10.5px] mb-3" style={{ color: "var(--text-secondary)" }}>
        Agree and the trade is filed, then make the swap in-game.
      </p>
      <ul className="space-y-3">
        {proposals.map((p) => (
          <ProposalRow
            key={p.id}
            p={p}
            myAccountId={myAccountId}
            friendName={friendName}
            busy={busyId === p.id}
            onAgree={onAgree}
            onDecline={onDecline}
            onCancel={onCancel}
            onComplete={onComplete}
            onShareCard={onShareCard}
          />
        ))}
      </ul>
    </section>
  );
}

function ProposalRow({
  p,
  myAccountId,
  friendName,
  busy,
  onAgree,
  onDecline,
  onCancel,
  onComplete,
  onShareCard,
}: {
  p: TradeProposal;
  myAccountId: number;
  friendName: string;
  busy: boolean;
  onAgree: (id: number) => void;
  onDecline: (id: number) => void;
  onCancel: (id: number) => void;
  onComplete: (p: TradeProposal) => void;
  onShareCard?: (pairing: TradePairing) => void;
}) {
  const iAmProposer = p.fromAccountId === myAccountId;
  // Re-frame give/get from the viewer's perspective (the proposer's "give" is
  // the recipient's "get").
  const mine = iAmProposer
    ? { giveId: p.giveSpeciesId, giveVar: p.giveVariant, getId: p.getSpeciesId, getVar: p.getVariant }
    : { giveId: p.getSpeciesId, giveVar: p.getVariant, getId: p.giveSpeciesId, getVar: p.giveVariant };
  const myCompleted = Boolean(iAmProposer ? p.fromCompletedAt : p.toCompletedAt);
  const theirCompleted = Boolean(iAmProposer ? p.toCompletedAt : p.fromCompletedAt);

  // Every row here IS an in-app ask; the card is the other way it may ALSO have
  // gone out. The pairing is oriented to the reader, which is what lets the same
  // swap be recognised whether it was exported from this row or off the Trade
  // Match card before the proposal existed.
  const pairing: TradePairing = {
    give: { speciesId: mine.giveId, variant: mine.giveVar },
    get: { speciesId: mine.getId, variant: mine.getVar },
  };
  const alsoShared = useTradeShareLog().has(tradePairingKey(pairing));

  return (
    <li
      className="rounded-2xl p-3"
      style={{
        background: "var(--surface-dark-6)",
        // The border carries the method: a proposal is an in-app ask, so it
        // wears that colour, and once a card has gone out as well the row picks
        // up the card colour on its leading edge. Chrome that matches the button
        // you pressed.
        border: `1px solid ${shareMethodAlpha("inApp", 0.45)}`,
        ...(alsoShared
          ? { borderLeft: `3px solid ${TRADE_SHARE_METHODS.card.color}` }
          : {}),
      }}
      data-testid={`proposal-${p.id}`}
      data-shared-as-card={alsoShared || undefined}
    >
      <div className="flex items-center gap-2 mb-2.5">
        <MiniSide label="You give" speciesId={mine.giveId} variant={mine.giveVar} />
        <ArrowRight className="w-4 h-4 shrink-0" style={{ color: "var(--accent-fill)" }} />
        <MiniSide label="You get" speciesId={mine.getId} variant={mine.getVar} />
      </div>

      {/* How this trade has been shared, in the same colours and glyphs as the
          buttons that do it. `inApp` is always lit — the row would not exist
          otherwise — and `card` lights once one has been sent.

          The offer stays put after that rather than turning into the chip: a
          card that has gone to one person can still go to another, and a
          control that vanishes once used cannot do that. */}
      <div className="flex items-center gap-1.5 mb-2 flex-wrap" data-testid={`proposal-share-chips-${p.id}`}>
        <ShareChip method="inApp" />
        {alsoShared && <ShareChip method="card" />}
        {onShareCard && (
          <button
            type="button"
            onClick={() => onShareCard(pairing)}
            data-testid={`proposal-share-card-${p.id}`}
            className="ml-auto inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-semibold min-h-[28px]"
            style={{
              background: shareMethodAlpha("card", 0.12),
              border: `1px solid ${shareMethodAlpha("card", 0.45)}`,
              color: TRADE_SHARE_METHODS.card.color,
            }}
          >
            <TRADE_SHARE_METHODS.card.Icon className="w-3 h-3" />
            {alsoShared ? "Send again" : "Share it another way"}
          </button>
        )}
      </div>

      {p.status === "pending" && iAmProposer && (
        <div className="flex items-center justify-between gap-2">
          <StatusText icon={Clock}>Waiting for {friendName} to agree</StatusText>
          <GhostBtn onClick={() => onCancel(p.id)} busy={busy} testid={`proposal-cancel-${p.id}`}>
            <X className="w-3.5 h-3.5" /> Cancel
          </GhostBtn>
        </div>
      )}

      {p.status === "pending" && !iAmProposer && (
        <div className="flex items-stretch gap-2">
          <PrimaryBtn onClick={() => onAgree(p.id)} busy={busy} testid={`proposal-agree-${p.id}`}>
            <Check className="w-3.5 h-3.5" /> Agree
          </PrimaryBtn>
          <GhostBtn onClick={() => onDecline(p.id)} busy={busy} testid={`proposal-decline-${p.id}`}>
            <X className="w-3.5 h-3.5" /> Decline
          </GhostBtn>
        </div>
      )}

      {p.status === "agreed" && (
        <>
          <div className="flex items-center gap-3 mb-2">
            <Tick done={myCompleted}>You</Tick>
            <Tick done={theirCompleted}>{friendName}</Tick>
          </div>
          <div className="flex items-stretch gap-2">
            {!myCompleted ? (
              <PrimaryBtn onClick={() => onComplete(p)} busy={busy} testid={`proposal-complete-${p.id}`}>
                <Check className="w-3.5 h-3.5" /> Mark completed
              </PrimaryBtn>
            ) : (
              <StatusText icon={Clock}>Waiting for {friendName} to confirm</StatusText>
            )}
            {iAmProposer && !myCompleted && (
              <GhostBtn onClick={() => onCancel(p.id)} busy={busy} testid={`proposal-cancel-${p.id}`}>
                <X className="w-3.5 h-3.5" /> Cancel
              </GhostBtn>
            )}
          </div>
        </>
      )}

      {p.status === "completed" && (
        <StatusText icon={Check} tone="var(--accent-fill)">
          Trade complete
        </StatusText>
      )}
    </li>
  );
}

/** A "this went out this way" marker, in that method's colour and glyph. */
function ShareChip({ method }: { method: TradeShareMethod }) {
  const meta = TRADE_SHARE_METHODS[method];
  const { Icon } = meta;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9.5px] font-bold"
      style={{
        background: shareMethodAlpha(method, 0.14),
        border: `1px solid ${shareMethodAlpha(method, 0.4)}`,
        color: meta.color,
      }}
      data-testid={`share-chip-${method}`}
    >
      <Icon className="w-2.5 h-2.5" aria-hidden />
      {meta.chip}
    </span>
  );
}

function MiniSide({ label, speciesId, variant }: { label: string; speciesId: number; variant: string }) {
  return (
    <div className="flex-1 min-w-0 flex items-center gap-2">
      <div
        className="w-10 h-10 rounded-lg flex items-center justify-center overflow-clip p-0.5 shrink-0"
        style={{ background: "var(--surface-dark-4)", border: "1px solid var(--surface-chip-border)" }}
      >
        <WishlistSprite
          entry={{ variant: variant as PokedexVariant, dexNumber: speciesId, addedAt: 0 }}
          fill
          ariaLabel={speciesName(speciesId)}
        />
      </div>
      <div className="min-w-0">
        <p className="text-[8.5px] uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>
          {label}
        </p>
        <p className="text-[11.5px] font-bold truncate" style={{ color: "var(--text-primary)" }}>
          {speciesName(speciesId)}
        </p>
        {/* Both trainers see this row, and one of them is being asked to agree
            to it. If the thing being agreed to is a chance, it says so. */}
        <ChanceOnlyBadge variant={variant} className="mt-0.5" />
      </div>
    </div>
  );
}

function PrimaryBtn({
  onClick,
  busy,
  testid,
  children,
}: {
  onClick: () => void;
  busy: boolean;
  testid: string;
  children: React.ReactNode;
}) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={busy}
      className="flex-1 v2-neon-cta py-2 text-[12px] min-h-[34px] flex items-center justify-center gap-1.5 disabled:opacity-60"
    >
      {children}
    </button>
  );
}

function GhostBtn({
  onClick,
  busy,
  testid,
  children,
}: {
  onClick: () => void;
  busy: boolean;
  testid: string;
  children: React.ReactNode;
}) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={busy}
      className="px-3 py-2 rounded-xl text-[12px] min-h-[34px] flex items-center justify-center gap-1.5 disabled:opacity-60"
      style={{ background: "var(--surface-dark-4)", border: "1px solid var(--surface-chip-border)", color: "var(--text-secondary)" }}
    >
      {children}
    </button>
  );
}

function StatusText({
  icon: Icon,
  tone,
  children,
}: {
  icon: typeof Clock;
  tone?: string;
  children: React.ReactNode;
}) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px]" style={{ color: tone ?? "var(--text-secondary)" }}>
      <Icon className="w-3.5 h-3.5" /> {children}
    </span>
  );
}

function Tick({ done, children }: { done: boolean; children: React.ReactNode }) {
  return (
    <span
      className="inline-flex items-center gap-1 text-[10.5px]"
      style={{ color: done ? "var(--accent-fill)" : "var(--text-tertiary)" }}
    >
      <span
        className="w-4 h-4 rounded-full flex items-center justify-center"
        style={{
          border: `1.5px solid ${done ? "var(--accent-fill)" : "var(--surface-chip-border)"}`,
          background: done ? "var(--accent-subtle)" : "transparent",
        }}
      >
        {done && <Check className="w-2.5 h-2.5" />}
      </span>
      {children}
    </span>
  );
}
